import type { CSSProperties } from "react";
import http from "./http";

export type StationsTextFontSizePreset = "sm" | "md" | "lg";
export type StationsTextFontWeight = "normal" | "bold";
export type StationsTextFontStyle = "normal" | "italic";

export type StationsTextStylePreset = {
	fontSizePreset: StationsTextFontSizePreset;
	fontWeight: StationsTextFontWeight;
	fontStyle: StationsTextFontStyle;
};

export type StationsTextPaletteEntry = {
	token: string;
	label: string;
	value: string;
};

export type StationsTextEntry = {
	key: string;
	screen: string;
	group: string;
	defaultText: string;
	currentText: string;
	/** Bản dịch theo mã ngôn ngữ (vi, …); tiếng Anh là currentText/defaultText. */
	translations: Record<string, string>;
	stylePreset: StationsTextStylePreset;
	colorToken: string;
	status: "draft" | "published";
	updatedAt?: string | null;
};

export type StationsTextRegistry = {
	version: number;
	palette: StationsTextPaletteEntry[];
	languages: string[];
	items: StationsTextEntry[];
	updatedAt?: string | null;
	publishedAt?: string | null;
};

export type StationsTextRegistryAdminPayload = {
	draft: StationsTextRegistry;
	published: StationsTextRegistry;
	palette: StationsTextPaletteEntry[];
};

export type StationsTextDetection = {
	fingerprint: string;
	file: string;
	text: string;
	suggestedScreen: string;
	suggestedGroup: string;
	status: "new" | "ignored" | "resolved";
	note?: string;
	ignoredAt?: string | null;
	updatedAt?: string | null;
};

export const DEFAULT_TEXT_PALETTE: StationsTextPaletteEntry[] = [
	{ token: "default", label: "Default", value: "#0f172a" },
	{ token: "white", label: "White", value: "#ffffff" },
	{ token: "primary", label: "Primary", value: "#2563eb" },
	{ token: "secondary", label: "Secondary", value: "#0f766e" },
	{ token: "muted", label: "Muted", value: "#64748b" },
	{ token: "success", label: "Success", value: "#15803d" },
	{ token: "danger", label: "Danger", value: "#dc2626" },
	{ token: "warning", label: "Warning", value: "#b45309" },
];

const DEFAULT_STYLE_PRESET: StationsTextStylePreset = {
	fontSizePreset: "md",
	fontWeight: "normal",
	fontStyle: "normal",
};

function normalizeStylePreset(value: Partial<StationsTextStylePreset> | undefined): StationsTextStylePreset {
	return {
		fontSizePreset: value?.fontSizePreset === "sm" || value?.fontSizePreset === "lg" ? value.fontSizePreset : "md",
		fontWeight: value?.fontWeight === "bold" ? "bold" : "normal",
		fontStyle: value?.fontStyle === "italic" ? "italic" : "normal",
	};
}

export const DEFAULT_TEXT_LANGUAGES = ["en", "vi"];

/** Mã ngôn ngữ cần dịch (bỏ "en" vì đó là bản gốc). */
export function translationLanguages(registry: Pick<StationsTextRegistry, "languages"> | null | undefined): string[] {
	const raw = registry?.languages;
	const list = Array.isArray(raw) && raw.length > 0 ? raw : DEFAULT_TEXT_LANGUAGES;
	return list.filter((code) => code && code !== "en");
}

function normalizeTranslations(value: unknown): Record<string, string> {
	if (!value || typeof value !== "object" || Array.isArray(value)) return {};
	const out: Record<string, string> = {};
	Object.entries(value as Record<string, unknown>).forEach(([lang, text]) => {
		if (typeof text === "string") out[lang] = text;
	});
	return out;
}

function normalizeEntry(
	value: Partial<StationsTextEntry> | undefined,
	status: "draft" | "published"
): StationsTextEntry | null {
	const key = String(value?.key || "").trim();
	if (!key) return null;

	return {
		key,
		screen: String(value?.screen || "general").trim() || "general",
		group: String(value?.group || "general").trim() || "general",
		defaultText: typeof value?.defaultText === "string" ? value.defaultText : "",
		currentText: typeof value?.currentText === "string" ? value.currentText : "",
		translations: normalizeTranslations(value?.translations),
		stylePreset: normalizeStylePreset(value?.stylePreset),
		colorToken: String(value?.colorToken || "default").trim() || "default",
		status,
		updatedAt: typeof value?.updatedAt === "string" ? value.updatedAt : null,
	};
}

export function normalizeStationsTextRegistry(
	value: Partial<StationsTextRegistry> | undefined,
	status: "draft" | "published"
): StationsTextRegistry {
	const rawPalette = value?.palette;
	const rawLanguages = value?.languages;
	const rawItems = value?.items;
	return {
		version: Number(value?.version || 1) || 1,
		palette: Array.isArray(rawPalette) && rawPalette.length > 0 ? rawPalette : DEFAULT_TEXT_PALETTE,
		languages:
			Array.isArray(rawLanguages) && rawLanguages.length > 0
				? rawLanguages.map((code) => String(code)).filter(Boolean)
				: DEFAULT_TEXT_LANGUAGES,
		items: Array.isArray(rawItems)
			? (rawItems.map((item) => normalizeEntry(item, status)).filter(Boolean) as StationsTextEntry[])
			: [],
		updatedAt: typeof value?.updatedAt === "string" ? value.updatedAt : null,
		publishedAt: typeof value?.publishedAt === "string" ? value.publishedAt : null,
	};
}

export function normalizeDetection(value: Partial<StationsTextDetection> | undefined): StationsTextDetection | null {
	const fingerprint = String(value?.fingerprint || "").trim();
	if (!fingerprint) return null;

	return {
		fingerprint,
		file: String(value?.file || "").trim(),
		text: String(value?.text || "").trim(),
		suggestedScreen: String(value?.suggestedScreen || "general").trim() || "general",
		suggestedGroup: String(value?.suggestedGroup || "general").trim() || "general",
		status: value?.status === "ignored" || value?.status === "resolved" ? value.status : "new",
		note: typeof value?.note === "string" ? value.note : "",
		ignoredAt: typeof value?.ignoredAt === "string" ? value.ignoredAt : null,
		updatedAt: typeof value?.updatedAt === "string" ? value.updatedAt : null,
	};
}

export function buildStationsTextStyle(
	entry: Partial<StationsTextEntry>,
	palette: StationsTextPaletteEntry[] = DEFAULT_TEXT_PALETTE
): CSSProperties {
	const paletteMap = new Map(palette.map((item) => [item.token, item.value]));
	const fontSizeMap: Record<StationsTextFontSizePreset, string> = {
		sm: "0.875rem",
		md: "1rem",
		lg: "1.125rem",
	};
	const stylePreset = normalizeStylePreset(entry.stylePreset);

	return {
		fontSize: fontSizeMap[stylePreset.fontSizePreset] || fontSizeMap.md,
		fontWeight: stylePreset.fontWeight,
		fontStyle: stylePreset.fontStyle,
		color: paletteMap.get(String(entry.colorToken || "default")) || paletteMap.get("default") || "#0f172a",
		textDecoration: "none",
	};
}

export async function getStationsTextRegistryAdmin(): Promise<StationsTextRegistryAdminPayload> {
	const { data } = await http.get("/utility/stations-text-registry");
	const payload = data?.data || {};

	return {
		draft: normalizeStationsTextRegistry(payload?.draft, "draft"),
		published: normalizeStationsTextRegistry(payload?.published, "published"),
		palette: Array.isArray(payload?.palette) && payload.palette.length > 0 ? payload.palette : DEFAULT_TEXT_PALETTE,
	};
}

export async function saveStationsTextDraft(registry: StationsTextRegistry) {
	const { data } = await http.post("/utility/stations-text-registry/draft", { data: registry });
	return normalizeStationsTextRegistry(data?.data, "draft");
}

export async function publishStationsText() {
	const { data } = await http.post("/utility/stations-text-registry/publish");
	return normalizeStationsTextRegistry(data?.data, "published");
}

export async function getStationsTextDetections(): Promise<StationsTextDetection[]> {
	const { data } = await http.get("/utility/stations-text-detections");
	return Array.isArray(data?.data)
		? data.data.map((item: StationsTextDetection) => normalizeDetection(item)).filter(Boolean)
		: [];
}

export async function ignoreStationsTextDetection(fingerprint: string, note = "") {
	const { data } = await http.post("/utility/stations-text-detections/ignore", {
		data: { fingerprint, note },
	});
	return Array.isArray(data?.data)
		? data.data.map((item: StationsTextDetection) => normalizeDetection(item)).filter(Boolean)
		: [];
}
